import React, { Component } from "react";
import { action, observable } from "mobx";
import { observer } from "mobx-react";

import { debounce } from "@formant/core/common/debounce";
import { duration } from "@formant/core/model/duration";
import { Fader } from "@formant/ui/component/common/Fader";
import { Measure } from "@formant/ui/component/common/Measure";

import { WindowResizer } from "./common/WindowResizer";
import * as styles from "./Tooltip.scss";

const margin = 30;
const placeholderSize = 200;

@observer
export class Tooltip extends Component {
    @observable
    private mouse = {
        x: 0,
        y: 0,
    };
    @observable
    private visible = false;
    @observable
    private text = "";
    @observable
    private windowWidth = 0;
    @observable
    private windowHeight = 0;
    @observable
    private width = 0;
    @observable
    private height = 0;

    private show = debounce((visible: boolean) => {
        this.visible = visible;
    }, 0.3 * duration.second);

    private hide = debounce(() => {
        this.visible = false;
    }, 3 * duration.second);

    public componentDidMount() {
        document.addEventListener("mousemove", this.onMouseMove);
        document.addEventListener("mouseover", this.onMouseEnter);
    }

    public onComponentWillUnmount() {
        document.removeEventListener("mousemove", this.onMouseMove);
        document.removeEventListener("mouseover", this.onMouseEnter);
    }

    public render() {
        const { width, height, windowWidth, windowHeight, mouse, text } = this;
        const { x, y } = mouse;

        const top =
            y + height + margin > windowHeight
                ? y - height - margin
                : y + margin;

        const left =
            x + Math.max(placeholderSize, width) > windowWidth ? undefined : x;
        const right =
            x + Math.max(placeholderSize, width) > windowWidth
                ? windowWidth - x
                : undefined;

        return (
            <>
                <WindowResizer onResize={this.onResizeWindow} />
                <Fader
                    visible={this.visible}
                    className={styles.tooltip}
                    style={{ top, left, right }}
                >
                    <Measure
                        onMeasureResize={this.onResize}
                        className={styles.content}
                    >
                        {text}
                    </Measure>
                </Fader>
            </>
        );
    }

    @action
    private onMouseMove = (event: MouseEvent) => {
        const { visible } = this;
        const { clientX, clientY } = event;

        if (!visible) {
            return;
        }

        this.mouse = {
            x: clientX,
            y: clientY,
        };

        this.hide();
    };

    @action
    private onMouseEnter = (event: MouseEvent) => {
        const { target, clientX, clientY } = event;

        event.stopPropagation();

        if (!(target instanceof Element)) {
            this.show(false);
            return;
        }

        const element = target.closest("[data-tooltip]") || undefined;
        if (!element) {
            this.show(false);
            return;
        }

        this.text = element.getAttribute("data-tooltip") || "";

        this.mouse = {
            x: clientX,
            y: clientY,
        };

        this.show(true);
    };

    @action
    private onResize = (width: number, height: number) => {
        this.width = width;
        this.height = height;
    };

    @action
    private onResizeWindow = () => {
        const { innerHeight, innerWidth } = window;
        this.windowWidth = innerWidth;
        this.windowHeight = innerHeight;
    };
}
